import { z } from 'zod';
import { AskAnswerSchema, AskSourceSchema } from './ask';

/**
 * Drafting (ADR-037). A matter holds anonymised facts; the platform analyses the issues and
 * law from the library and proposes documents; with the user's go-ahead it drafts them; the
 * user edits, asks for further work, and (lawyers only) approves. Facts use placeholders such
 * as [Client]: real client content awaits the confidential-data gate (SECURITY-AND-PRIVACY).
 */

export const DRAFT_TYPES = [
  'letter_before_action',
  'reply_to_demand',
  'legal_opinion',
  'agreement',
  'statement_of_claim',
  'defence',
  'affidavit',
  'motion',
  'writ_of_summons',
  'originating_summons',
  'filing_checklist',
] as const;
export const DraftTypeSchema = z.enum(DRAFT_TYPES);
export type DraftType = z.infer<typeof DraftTypeSchema>;

export const DRAFT_TYPE_LABEL: Record<DraftType, string> = {
  letter_before_action: 'Letter before action (letter of demand)',
  reply_to_demand: 'Reply to a letter of demand',
  legal_opinion: 'Legal opinion (letter of advice)',
  agreement: 'Agreement',
  statement_of_claim: 'Statement of claim',
  defence: 'Defence',
  affidavit: 'Affidavit',
  motion: 'Notice of motion',
  writ_of_summons: 'Writ of summons',
  originating_summons: 'Originating summons',
  filing_checklist: 'Filing checklist',
};

/** Court documents: drafted only when the court rules they follow are in the library. */
export const PLEADING_TYPES: readonly DraftType[] = [
  'statement_of_claim',
  'defence',
  'affidavit',
  'motion',
  'writ_of_summons',
  'originating_summons',
  'filing_checklist',
];

/**
 * Filing packages (ADR-040): the documents that start proceedings, drafted together in this
 * order so each is consistent with those before it, and exported as one bundle.
 */
export const DRAFT_PACKAGES = {
  commence_by_writ: {
    label: 'Start a High Court action by writ',
    description:
      'Writ of summons, statement of claim and a filing checklist under the High Court Rules 2007, drafted together and kept consistent.',
    documents: [
      { type: 'writ_of_summons', title: 'Writ of summons' },
      { type: 'statement_of_claim', title: 'Statement of claim' },
      { type: 'filing_checklist', title: 'Filing checklist' },
    ],
  },
  commence_by_originating_summons: {
    label: 'Start proceedings by originating summons',
    description:
      'Originating summons, a supporting affidavit and a filing checklist under the High Court Rules 2007, for applications the Rules require to be begun that way.',
    documents: [
      { type: 'originating_summons', title: 'Originating summons' },
      { type: 'affidavit', title: 'Affidavit in support' },
      { type: 'filing_checklist', title: 'Filing checklist' },
    ],
  },
} as const satisfies Record<
  string,
  { label: string; description: string; documents: readonly { type: DraftType; title: string }[] }
>;
export const DRAFT_PACKAGE_IDS = Object.keys(DRAFT_PACKAGES) as (keyof typeof DRAFT_PACKAGES)[];
export const DraftPackageIdSchema = z.enum(
  DRAFT_PACKAGE_IDS as [keyof typeof DRAFT_PACKAGES, ...(keyof typeof DRAFT_PACKAGES)[]],
);
export type DraftPackageId = z.infer<typeof DraftPackageIdSchema>;

export const AddPackageRequestSchema = z.object({ package: DraftPackageIdSchema }).strict();

export const DRAFTING_DAILY_LIMIT = 10;
export const DRAFT_BODY_MAX = 60_000;
export const DRAFT_VERSION_LIMIT = 12;

/** "[Client]", "[Landlord 2]": placeholders, never real names. */
const PLACEHOLDER = /^\[[A-Za-z0-9 .,'’&()-]{1,40}\]$/;

export const MatterIntakeSchema = z
  .object({
    title: z.string().trim().min(3).max(120),
    matterType: z.string().trim().min(3).max(120),
    acting: z.string().trim().min(2).max(300),
    parties: z
      .array(
        z
          .object({
            label: z.string().trim().regex(PLACEHOLDER, 'Use a placeholder such as [Client]'),
            role: z.string().trim().min(2).max(160),
          })
          .strict(),
      )
      .min(1)
      .max(10),
    facts: z.string().trim().min(40).max(8000),
    objective: z.string().trim().min(5).max(2000),
    /** The user confirms there are no real names or identifying details. */
    anonymised: z.literal(true),
  })
  .strict();
export type MatterIntake = z.infer<typeof MatterIntakeSchema>;

export const PlannedDocumentSchema = z.object({
  id: z.string(),
  type: DraftTypeSchema,
  title: z.string(),
  purpose: z.string(),
  recommended: z.boolean(),
  /** Why it cannot be drafted yet (e.g. court rules not in the library), or null. */
  blockedReason: z.string().nullable(),
  /** The filing package it belongs to (ADR-040), if any. */
  packageId: DraftPackageIdSchema.nullable().optional(),
});
export type PlannedDocument = z.infer<typeof PlannedDocumentSchema>;

export const DraftPlanSchema = z.object({
  /** The cited analysis of the issues and applicable law, from the research step. */
  analysis: AskAnswerSchema,
  issues: z.array(z.object({ issue: z.string(), note: z.string() })),
  documents: z.array(PlannedDocumentSchema),
  /** Information the drafts will need that the facts do not give. */
  missing: z.array(z.string()),
  createdAt: z.string(),
});
export type DraftPlan = z.infer<typeof DraftPlanSchema>;

export const DraftVersionSchema = z.object({
  version: z.number().int(),
  kind: z.enum(['ai_draft', 'revision', 'edit']),
  body: z.string(),
  sources: z.array(AskSourceSchema),
  /** The user's instruction, for a revision. */
  instruction: z.string().nullable(),
  byName: z.string().nullable(),
  createdAt: z.string(),
});
export type DraftVersion = z.infer<typeof DraftVersionSchema>;

export const MatterDraftSchema = z.object({
  id: z.string(),
  matterId: z.string(),
  planId: z.string(),
  type: DraftTypeSchema,
  title: z.string(),
  status: z.enum(['ai_draft', 'edited', 'approved']),
  currentVersion: z.number().int(),
  versions: z.array(DraftVersionSchema),
  approval: z
    .object({ version: z.number().int(), byName: z.string().nullable(), at: z.string() })
    .nullable(),
  model: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MatterDraft = z.infer<typeof MatterDraftSchema>;

export const MatterSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  /** Practice in a Learning workspace: never approved, labelled as practice. */
  practice: z.boolean(),
  title: z.string(),
  ownerName: z.string().nullable(),
  intake: MatterIntakeSchema,
  plan: DraftPlanSchema.nullable(),
  drafts: z.array(MatterDraftSchema),
  canApprove: z.boolean(),
  /** Shared with the firm workspace's lawyers (ADR-042); only the author shares or deletes. */
  sharedWithWorkspace: z.boolean(),
  isOwner: z.boolean(),
  canShare: z.boolean(),
  /** Client documents can be added to this matter (ADR-044): uploads switched on, a firm matter. */
  documentsEnabled: z.boolean(),
  /** Drafting tasks left today; null when not known (a plain read). */
  remainingToday: z.number().int().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Matter = z.infer<typeof MatterSchema>;

export const MatterSummarySchema = MatterSchema.pick({
  id: true,
  title: true,
  practice: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  matterType: z.string(),
  drafts: z.number().int(),
  planned: z.boolean(),
  ownerName: z.string().nullable(),
  isOwner: z.boolean(),
  sharedWithWorkspace: z.boolean(),
});
export type MatterSummary = z.infer<typeof MatterSummarySchema>;

export const MatterListSchema = z.object({ matters: z.array(MatterSummarySchema) });
export type MatterList = z.infer<typeof MatterListSchema>;

export const DraftRequestSchema = z
  .object({
    documentId: z.string().min(1).max(40),
    /** Extra directions for this draft, optional. */
    instruction: z.string().trim().max(2000).optional(),
  })
  .strict();

export const ReviseDraftRequestSchema = z
  .object({ instruction: z.string().trim().min(5).max(2000), baseVersion: z.number().int() })
  .strict();

export const EditDraftRequestSchema = z
  .object({ body: z.string().trim().min(20).max(DRAFT_BODY_MAX), baseVersion: z.number().int() })
  .strict();

export const SharingRequestSchema = z.object({ shared: z.boolean() }).strict();

export const ApproveDraftRequestSchema = z.object({ version: z.number().int() }).strict();
